import { z } from "zod";
import type { Operation } from "../types";

// Multi-tenant M2 — operator surface for Path C convergence (CONVERGENCE-PRD
// §12). Read-only status + a manual "sync now" trigger. The auth token is NEVER
// part of this contract — it lives in node env only (rule 7).

const syncConnState = z.enum([
  "single-player",
  "connected",
  "degraded",
  "offline",
]);

export const fleetNodeSchema = z.object({
  userId: z.number().int(),
  handle: z.string(),
  userDisplayName: z.string(),
  role: z.string(),
  deviceId: z.string().nullable(),
  deviceName: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  paired: z.boolean(),
});
export type FleetNode = z.infer<typeof fleetNodeSchema>;

const statusResponse = z.object({
  state: syncConnState,
  replica: z.boolean(),
  lastSyncedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  intervalMs: z.number().int(),
  writeVolume: z.number().int(),
  openConflicts: z.number().int(),
  nodes: z.array(fleetNodeSchema),
});
export type SyncStatusResponse = z.infer<typeof statusResponse>;

const nowResponse = z.object({
  /** Did the triggered sync reach the primary? false when degraded/offline or single-player. */
  synced: z.boolean(),
  state: syncConnState,
  lastSyncedAt: z.string().nullable(),
});
export type SyncNowResponse = z.infer<typeof nowResponse>;

const statusOp: Operation<void, void, void, SyncStatusResponse> = {
  method: "GET",
  path: () => "/api/sync/status",
  response: statusResponse,
};

const nowOp: Operation<void, void, void, SyncNowResponse> = {
  method: "POST",
  path: () => "/api/sync/now",
  response: nowResponse,
  invalidates: [statusOp],
};

// --- TEAM-UX-PRD §5.1/§5.5 — node config write + token rotation. Owner-or-solo
// gated; not MCP tools. The token is written to sync.env (0600), never the DB.
const connectBody = z.object({
  url: z.string().url(),
  token: z.string().optional(),
  intervalSeconds: z.number().int().min(1).max(3600).optional(),
});
const connectResponse = z.object({
  ok: z.boolean(),
  syncEnvPath: z.string(),
  sourceCommand: z.string(),
  reason: z.string().optional(),
});
export type SyncConnectResponse = z.infer<typeof connectResponse>;
const connectOp: Operation<
  void,
  void,
  z.infer<typeof connectBody>,
  SyncConnectResponse
> = {
  method: "POST",
  path: () => "/api/sync/connect",
  body: connectBody,
  response: connectResponse,
  invalidates: [statusOp],
};

const rotateResponse = z.object({
  ok: z.boolean(),
  expiresAt: z.string().nullable(),
  reason: z.string().optional(),
});
export type SyncRotateResponse = z.infer<typeof rotateResponse>;
const rotateOp: Operation<void, void, void, SyncRotateResponse> = {
  method: "POST",
  path: () => "/api/sync/rotate-token",
  response: rotateResponse,
  invalidates: [statusOp],
};

// Web join (TEAM-UX-PRD §5.4) — the joiner opens /join?bundle=… on their own
// install; this decodes the bundle, writes sync.env, and stashes the invite.
const joinBody = z.object({ bundle: z.string().min(1) });
const joinResponse = z.object({
  ok: z.boolean(),
  syncEnvPath: z.string().nullable(),
  sourceCommand: z.string().nullable(),
  hasToken: z.boolean(),
  reason: z.string().optional(),
});
export type SyncJoinResponse = z.infer<typeof joinResponse>;
const joinOp: Operation<void, void, z.infer<typeof joinBody>, SyncJoinResponse> = {
  method: "POST",
  path: () => "/api/sync/join",
  body: joinBody,
  response: joinResponse,
  invalidates: [statusOp],
};

// --- Plane-T (team, conflict-free) on-ramp (MULTIPLAYER-COMPLETION-PLAN WS3/WS4).
// Team sync is the field-level, nothing-is-lost merge plane (cr-sqlite CRR).
// It only runs where the cr-sqlite extension is vendored (Apple-Silicon Macs
// today), so the switch first asks whether this platform can run it, then the
// owner enables/disables it. Owner-or-solo gated; writes sync.env (0600), never
// the DB. Mode-exclusive with a solo primary — enabling clears solo config.

// WS4 — the platform capability probe the switch reads before offering itself.
const teamCapabilitiesResponse = z.object({
  /** Can this machine run conflict-free team sync (cr-sqlite vendored)? */
  available: z.boolean(),
  /** "<platform>-<arch>", e.g. "darwin-arm64". */
  platform: z.string(),
  /** Human reason when unavailable ("Team sync needs an Apple-Silicon Mac …"); null when available. */
  reason: z.string().nullable(),
  /** Is EXCELLENT_SYNC_MODE=team currently configured on this node? */
  enabled: z.boolean(),
  /** The configured relay URL, if team mode is set. */
  relayUrl: z.string().nullable(),
});
export type SyncTeamCapabilitiesResponse = z.infer<typeof teamCapabilitiesResponse>;
const teamCapabilitiesOp: Operation<void, void, void, SyncTeamCapabilitiesResponse> = {
  method: "GET",
  path: () => "/api/sync/team-capabilities",
  response: teamCapabilitiesResponse,
};

// WS3 — turn team mode on. Writes EXCELLENT_SYNC_MODE=team + relay, mints/stamps
// this node's device key so the boot loop can pair. Takes effect on next start.
const enableTeamBody = z.object({
  relayUrl: z.string().url(),
});
const enableTeamResponse = z.object({
  ok: z.boolean(),
  /** Present on success — where the config was written. */
  syncEnvPath: z.string().nullable(),
  sourceCommand: z.string().nullable(),
  /** This node's device id + public key to hand the relay operator (devices allowlist). */
  deviceId: z.string().nullable(),
  publicKey: z.string().nullable(),
  /** Set when ok:false — "platform-unsupported" | "no-device" | "no-workspace" | "owner-only". */
  reason: z.string().optional(),
});
export type SyncEnableTeamResponse = z.infer<typeof enableTeamResponse>;
const enableTeamOp: Operation<
  void,
  void,
  z.infer<typeof enableTeamBody>,
  SyncEnableTeamResponse
> = {
  method: "POST",
  path: () => "/api/sync/enable-team",
  body: enableTeamBody,
  response: enableTeamResponse,
  invalidates: [statusOp, teamCapabilitiesOp],
};

// WS3 — turn team mode off (back to solo/single-player). cr-sqlite shadows stay
// dormant and harmless; `excellent-mcp sync shadows prune` clears them fully.
const disableTeamResponse = z.object({
  ok: z.boolean(),
  syncEnvPath: z.string().nullable(),
  reason: z.string().optional(),
});
export type SyncDisableTeamResponse = z.infer<typeof disableTeamResponse>;
const disableTeamOp: Operation<void, void, void, SyncDisableTeamResponse> = {
  method: "POST",
  path: () => "/api/sync/disable-team",
  response: disableTeamResponse,
  invalidates: [statusOp, teamCapabilitiesOp],
};

export const sync = {
  status: statusOp,
  now: nowOp,
  connect: connectOp,
  rotateToken: rotateOp,
  join: joinOp,
  teamCapabilities: teamCapabilitiesOp,
  enableTeam: enableTeamOp,
  disableTeam: disableTeamOp,
};
